import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signout } from '@/helpers/authApi';
import { useDashboardIdentity } from '@/lib/auth/dashboard-context';
import { DEMO_ROLES } from '@/lib/auth/demo-roles';
import type { DashboardLanguage } from './language';

const labels = {
  en: { account: 'Account', accountMenu: 'Account menu', notifications: 'Notifications', clear: "You're all caught up", yourAccount: 'Your account', settings: 'Settings', website: 'Back to website', signOut: 'Sign out', signingOut: 'Signing out…', signOutError: 'Could not sign out. Try again.' },
  km: { account: 'គណនី', accountMenu: 'ម៉ឺនុយគណនី', notifications: 'ការជូនដំណឹង', clear: 'មិនមានការជូនដំណឹងថ្មីទេ', yourAccount: 'គណនីរបស់អ្នក', settings: 'ការកំណត់', website: 'ត្រឡប់ទៅគេហទំព័រ', signOut: 'ចាកចេញ', signingOut: 'កំពុងចាកចេញ…', signOutError: 'មិនអាចចាកចេញបានទេ។ សូមព្យាយាមម្តងទៀត។' },
  zh: { account: '账户', accountMenu: '账户菜单', notifications: '通知', clear: '暂无新通知', yourAccount: '我的账户', settings: '设置', website: '返回网站', signOut: '退出登录', signingOut: '正在退出…', signOutError: '无法退出，请重试。' },
};

export default function HeaderActions({ language }: { language: DashboardLanguage }) {
  const identity = useDashboardIdentity();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLButtonElement>(null);
  const accountRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<'notifications' | 'account' | null>(null);
  const [notificationPosition, setNotificationPosition] = useState({ left: 0, width: 256 });
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState('');
  const t = labels[language];
  const email = identity?.email ?? '';
  const initials = email ? email.slice(0, 2).toUpperCase() : 'LB';
  const role = identity?.platformRoles?.[0] ?? identity?.memberships?.[0]?.role ?? '';
  const roleName = DEMO_ROLES.find((item) => item.role === role)?.name ?? role.replaceAll('_', ' ');

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(null);
        (open === 'account' ? accountRef : notificationsRef).current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  async function handleSignOut() {
    setSigningOut(true);
    setError('');
    try {
      await signout();
      setOpen(null);
      navigate('/login', { replace: true });
    } catch {
      setError(t.signOutError);
      setSigningOut(false);
    }
  }

  function toggleNotifications() {
    if (open === 'notifications') {
      setOpen(null);
      return;
    }
    const trigger = notificationsRef.current?.getBoundingClientRect();
    const container = rootRef.current?.getBoundingClientRect();
    if (trigger && container) {
      const width = Math.min(256, window.innerWidth - 16);
      const centered = trigger.left + trigger.width / 2 - width / 2;
      const left = Math.min(Math.max(centered, 8), window.innerWidth - width - 8);
      setNotificationPosition({ left: left - container.left, width });
    }
    setOpen('notifications');
  }

  const itemClass = 'dashboard-header-menu-item flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-3 text-left text-sm text-foreground-800 focus-visible:outline-none';

  return (
    <div ref={rootRef} className="relative flex shrink-0 items-center gap-1 sm:gap-2">
      <button ref={notificationsRef} type="button" aria-label={t.notifications} aria-expanded={open === 'notifications'} onClick={toggleNotifications} className="dashboard-header-icon-button flex size-11 cursor-pointer items-center justify-center rounded-full text-foreground-700 focus-visible:outline-none">
        <i className="ri-notification-3-line text-lg" aria-hidden="true" />
      </button>
      <button ref={accountRef} type="button" aria-label={t.accountMenu} aria-expanded={open === 'account'} onClick={() => { setError(''); setOpen((value) => value === 'account' ? null : 'account'); }} className="dashboard-header-account-trigger flex min-h-11 cursor-pointer items-center gap-2 rounded-full border p-1 pr-1 text-left focus-visible:outline-none lg:pr-2">
        <span className="dashboard-header-avatar flex size-9 shrink-0 items-center justify-center rounded-full border text-xs font-bold" aria-hidden="true">{initials}</span>
        <span className="hidden min-w-0 leading-tight lg:block">
          <span className="block max-w-36 truncate text-sm font-semibold text-foreground-950" title={email}>{email || t.account}</span>
          {roleName && <span className="block text-xs capitalize text-foreground-500">{roleName}</span>}
        </span>
        <i className={`ri-arrow-down-s-line hidden text-sm text-foreground-500 transition-transform duration-150 lg:block ${open === 'account' ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open === 'notifications' && (
        <section aria-label={t.notifications} className="dashboard-header-popover absolute top-full z-50 mt-2 rounded-xl border p-4" style={notificationPosition}>
          <p className="text-sm font-semibold text-foreground-950">{t.notifications}</p>
          <div role="status" className="flex flex-col items-center gap-2 px-2 py-5 text-center">
            <span className="dashboard-header-empty-icon flex size-10 items-center justify-center rounded-full"><i className="ri-notification-off-line text-lg" aria-hidden="true" /></span>
            <p className="text-sm text-foreground-600">{t.clear}</p>
          </div>
        </section>
      )}

      {open === 'account' && (
        <section aria-label={t.accountMenu} className="dashboard-header-popover absolute right-0 top-full z-50 mt-2 w-72 rounded-xl border p-1.5">
          <div className="flex items-center gap-3 border-b border-background-200 px-3 py-3">
            <span className="dashboard-header-avatar flex size-10 shrink-0 items-center justify-center rounded-full border text-xs font-bold" aria-hidden="true">{initials}</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground-950" title={email}>{email || t.account}</p>
              {roleName && <p className="mt-0.5 text-xs capitalize text-foreground-500">{roleName}</p>}
            </div>
          </div>
          <div className="py-1">
            <Link to="/account" onClick={() => setOpen(null)} className={itemClass}><i className="ri-user-line text-lg" aria-hidden="true" />{t.yourAccount}</Link>
            {identity?.dashboardPages?.includes('/dashboard/settings') && <Link to="/dashboard/settings" onClick={() => setOpen(null)} className={itemClass}><i className="ri-settings-3-line text-lg" aria-hidden="true" />{t.settings}</Link>}
            <Link to="/" onClick={() => setOpen(null)} className={itemClass}><i className="ri-home-4-line text-lg" aria-hidden="true" />{t.website}</Link>
          </div>
          <div className="border-t border-background-200 pt-1">
            {error && <p role="alert" className="px-3 py-2 text-xs text-secondary-800">{error}</p>}
            <button type="button" disabled={signingOut} onClick={() => void handleSignOut()} className={`${itemClass} disabled:cursor-wait disabled:opacity-60`}><i className="ri-logout-box-r-line text-lg" aria-hidden="true" />{signingOut ? t.signingOut : t.signOut}</button>
          </div>
        </section>
      )}
    </div>
  );
}
